import React, { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import {
  generateEvenlySpacedTicks,
  getLinearAxisTickCount,
} from "../../common/components/d3/d3ChartTickUtilities";

const TradesChart = ({ data, title, startDate, endDate }) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const innerChartRef = chartRef;

    // Setup dimensions and margins
    const margin = { top: 40, right: 20, bottom: 70, left: 60 }; // Increased bottom margin for rotated labels
    const containerWidth = innerChartRef?.current?.offsetWidth;
    const containerHeight = 400; // Default height if not specified
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(innerChartRef.current).select("svg").remove();

    // Create SVG container
    const svg = d3
      .select(innerChartRef.current)
      .append("svg")
      .attr("width", containerWidth)
      .attr("height", containerHeight)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Process data
    const formatDate = d3.timeFormat("%Y-%m-%d");

    // // Extract all unique dates from your data
    // const allDates = data.map(d => d.TransactionDate.split('T')[0]);

    // Generate an array of all days between startDate and endDate
    const dateRange = d3.timeDay.range(
      new Date(startDate),
      d3.timeDay.offset(new Date(endDate), 1)
    );

    // Create data with zero quantity for each day in the date range
    const dataByDay = dateRange.map((date) => ({
      date: date, // Use the date object directly
      quantity: data
        .filter((d) => d.TransactionDate.split("T")[0] === formatDate(date))
        .reduce((acc, curr) => acc + curr.Quantity, 0),
    }));

    // Set up scales
    const x = d3
      .scaleBand()
      .domain(dateRange.map((d) => formatDate(d))) // Ensure domain matches the tick format
      .range([0, width])
      .padding(0.1); // Adjust padding as needed for spacing between bars

    const y = d3
      .scaleLinear()
      .domain([
        0,
        Math.max(
          1,
          d3.max(dataByDay, (d) => d.quantity)
        ),
      ])
      .nice()
      .range([height, 0]);

    // Draw axes with smart tick spacing
    const formattedDateRange = dateRange.map((d) => formatDate(d));
    const maxXTicks = getLinearAxisTickCount(width, 80);
    const xTickValues = generateEvenlySpacedTicks(
      formattedDateRange,
      maxXTicks
    );

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(xTickValues) // Use evenly spaced tick values
          .tickFormat((d) => {
            // Format tick labels as desired (e.g., 'Jun 17')
            const date = d3.timeParse("%Y-%m-%d")(d);
            return d3.timeFormat("%b %d")(date);
          })
      )
      .attr("class", "d3-chart-axis")
      .selectAll("text")
      .attr("transform", "rotate(-45)") // Rotate labels 45 degrees
      .style("text-anchor", "end") // Align text to the end
      .style("font-size", "10px"); // Adjust font size for better fit

    // Generate optimal tick count for y-axis
    const maxYTicks = getLinearAxisTickCount(height, 40);
    svg
      .append("g")
      .call(d3.axisLeft(y).ticks(maxYTicks))
      .attr("class", "d3-chart-axis");

    // Create a tooltip element
    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "d3-chart-tooltip")
      .style("opacity", 0);

    tooltipRef.current = tooltip;

    // Draw bars with spacing
    svg
      .selectAll(".bar")
      .data(dataByDay)
      .enter()
      .append("rect")
      .attr("class", "bar dashboard-bar dashboard-bar-accent")
      .attr("x", (d) => x(formatDate(d.date)))
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("y", (d) => y(d.quantity))
      .attr("width", x.bandwidth())
      .attr("height", (d) => height - y(d.quantity))
      .attr("fill", "steelblue")
      .on("mouseover", function (event, d) {
        // Show tooltip
        tooltip.transition().duration(200).style("opacity", 1);
        tooltip.style("padding", "5px");
        tooltip.html(`
                    Date: <strong>${d3.timeFormat("%b %d")(d.date)}</strong><br>
                    Quantity: <strong>${d3.format(",")(d.quantity)}</strong>
                `);
      })
      .on("mousemove", function (event) {
        // Move tooltip with the mouse
        tooltip
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 28 + "px");
      })
      .on("mouseout", function () {
        // Hide tooltip
        tooltip.transition().duration(500).style("opacity", 0);
      });

    // Add chart title
    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", -10)
      .attr("text-anchor", "middle")
      .style("font-size", "16px")
      .style("font-weight", "bold")
      .attr("class", "d3-chart-title")
      .text(title);
  }, [data, title, startDate, endDate]);

  useEffect(() => {
    renderChart();

    return () => {
      // Remove the tooltip when the component unmounts
      if (tooltipRef.current) {
        tooltipRef.current.remove();
      }
    };
  }, [renderChart]);

  useEffect(() => {
    const innerChartRef = chartRef;

    let resizeTimeout = null;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          renderChart(); // Re-render the chart on resize
          resizeTimeout = null;
        }, 100); // Adjust throttle delay as needed
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (innerChartRef.current) {
      observer.observe(innerChartRef.current);
    }

    // Clean up observer and timeout on unmount
    return () => {
      if (innerChartRef.current) {
        observer.unobserve(innerChartRef.current);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart]);

  return (
    <div
      ref={chartRef}
      className="dashboard-d3-chart"
      style={{ width: "100%", height: "100%" }}
    ></div>
  );
};

export default TradesChart;







